import { isNil, get, reduce, isFunction } from 'lodash';
import {
  Injectable,
  Injector,
  Type,
  InjectionToken,
  AbstractType,
} from '@angular/core';

import {
  IStandWorkSheetItemFullData,
  IInformationData,
} from '@app/shared/types/entities/stand-worksheet/stand-worksheet.type';
import { GridRowData } from '@app/shared/types/ui/components/custom-grid.type';
import { DecimalNumber } from '@app/shared/common/decimal-number/decimal-number';
import { STAND_WORKSHEET_PDF_HEADER_VALUES } from '@app/shared/types/enums/stand-worksheet-pdf.enum';
import { IBaseEntityType } from '@app/shared/types/entities/base/base.type';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { StateService } from '../../state-service/state.service';
import { CALCULATION_VERSIONS } from './calculation.enums';

@Injectable()
export class DefaultStandWorksheetCalcService {
  public static VERSION: string = CALCULATION_VERSIONS.V1;

  @lazyInject(StateService) public stateService: StateService;

  public calculateExpectedQuantityColumn(
    item: IStandWorkSheetItemFullData,
    totalAvailable: DecimalNumber,
  ): DecimalNumber {
    // Expected Quantity = Total Available + During Event In - During Event Out +/- Adjustment- Ingredient Spoil
    // - Gratis - Terminal Qty - Post Closure Sales
    return new DecimalNumber(totalAvailable)
      .add(item.during_event_in || 0)
      .sub(item.during_event_out || 0)
      .add(item.adjustment || 0)
      .sub(item.gratis || 0)
      .sub(item.ingredient_spoil || 0)
      .sub(item.sale || 0)
      .sub(this.getPostClosureValueWithPreference(item.sale_post_closure));
  }

  public calculateTotalAvailableColumn(
    item: IStandWorkSheetItemFullData,
  ): DecimalNumber {
    // Begin Count + Pre-event In or MGR Starting Override
    return isNil(item.manager_begin)
      ? new DecimalNumber(item.begin || 0).add(item.pre_event_in || 0)
      : new DecimalNumber(item.manager_begin);
  }

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public calcBasePrice(
    item: IStandWorkSheetItemFullData,
    isUseSnapshot: boolean,
  ): DecimalNumber {
    if (isUseSnapshot) {
      return new DecimalNumber(
        isNil(item.base_unit_price) ? 0 : item.base_unit_price,
      );
    }

    if (isNil(item.parent_product_price) || isNil(item.recipe_measure_factor)) {
      return new DecimalNumber(0);
    }

    return new DecimalNumber(item.parent_product_price || 0).div(
      item.recipe_measure_factor,
    );
  }

  public calculatePriceAdjustmentColumn(
    item: IStandWorkSheetItemFullData,
  ): DecimalNumber {
    // Terminal Dollars + Post_Closure Dollars
    // + Discounts + Post-Closure Discounts
    // - tax - tax_post_closure
    // - Parent Product Price * Qty that all linked products sold
    const productCount = new DecimalNumber(item.product_count || 0).add(
      this.getPostClosureValueWithPreference(item.product_count_post_closure),
    );
    const parentProductDollars = new DecimalNumber(
      item.parent_product_price || 0,
    ).mul(productCount || 0);

    return new DecimalNumber(item.price || 0)
      .add(this.getPostClosureValueWithPreference(item.price_post_closure))
      .add(item.discount || 0)
      .add(this.getPostClosureValueWithPreference(item.discount_post_closure))
      .sub(item.tax || 0)
      .sub(this.getPostClosureValueWithPreference(item.tax_post_closure))
      .sub(parentProductDollars);
  }

  public calculatePhysicalQuantityColumn(
    item: IStandWorkSheetItemFullData,
    totalAvailable: DecimalNumber,
  ): DecimalNumber {
    // Physical Qty = Total Available + During Event In - During Event Out +/- Adjustment
    // - Ingredient Spoil - Gratis - Ending Count
    return new DecimalNumber(totalAvailable)
      .add(item.during_event_in || 0)
      .sub(item.during_event_out || 0)
      .add(item.adjustment || 0)
      .sub(item.gratis || 0)
      .sub(item.ingredient_spoil || 0)
      .sub(item.end || 0);
  }

  public calculateVarianceQuantityColumn(
    item: IStandWorkSheetItemFullData,
    expectedQty: DecimalNumber,
  ): DecimalNumber {
    // Variance Qty =  Ending Count - Expected Qty
    return new DecimalNumber(item.end || 0).sub(expectedQty || 0);
  }

  public calculatePhysicalDollarsColumn(
    item: IStandWorkSheetItemFullData,
    priceAdjustment: DecimalNumber,
    physicalQty: DecimalNumber,
    forceUseSnapShotData: boolean,
  ): DecimalNumber {
    // (Parent Product Price / recipe_measure_factor )*Physical Qty - discounts - post_closure_discounts
    // + tax + tax_post_closure + Price Adjustment;
    const physicalPrice = this.calcBasePrice(item, forceUseSnapShotData);

    const physicalDollars = new DecimalNumber(physicalPrice).mul(
      physicalQty || 0,
    );

    return physicalDollars
      .sub(item.discount || 0)
      .sub(this.getPostClosureValueWithPreference(item.discount_post_closure))
      .add(item.tax || 0)
      .add(this.getPostClosureValueWithPreference(item.tax_post_closure))
      .add(priceAdjustment);
  }

  public calculateVarianceDollarsColumn(
    item: IStandWorkSheetItemFullData,
    varianceQty: DecimalNumber,
    forceUseSnapShotData: boolean,
  ): DecimalNumber {
    // (Parent Product Price / recipe_measure_factor) * Variance Qty
    const variancePrice = this.calcBasePrice(item, forceUseSnapShotData);

    return variancePrice.mul(varianceQty);
  }

  public calcDuringEventTransfer(
    value: IStandWorkSheetItemFullData,
  ): DecimalNumber {
    const duringEventInKey = STAND_WORKSHEET_PDF_HEADER_VALUES.DuringEventIn;
    const duringEventOutKey = STAND_WORKSHEET_PDF_HEADER_VALUES.DuringEventOut;
    const duringEventIn = new DecimalNumber(
      isNil(get(value, duringEventInKey))
        ? 0
        : get(value, duringEventInKey).toString(),
    );
    const duringEventOut = new DecimalNumber(
      isNil(get(value, duringEventOutKey))
        ? 0
        : get(value, duringEventOutKey).toString(),
    );

    return duringEventIn.sub(duringEventOut);
  }

  public calcInformationCashTerminalDiff(
    informationData: IInformationData,
  ): DecimalNumber {
    const cashCountStr = get(informationData, 'cash_count');
    const decimalCashCount = new DecimalNumber(
      isNil(cashCountStr) ? 0 : cashCountStr,
    );

    return decimalCashCount
      .sub(get(informationData, 'cash', 0))
      .sub(
        this.getPostClosureValueWithPreference(
          get(informationData, 'cash_post_closure', 0),
        ),
      );
  }

  public calcInfoReceiptTotal(
    informationData: IInformationData,
  ): DecimalNumber {
    // Receipt Total = cash + cash_post_closure
    // + (credit + credit_post_closure + debit+ debit_post_closure + gift + gift_post_closure)
    // The fields in the top row are already calculated
    // in credit_debit_gift_total and credit_debit_gift_post_closure_total
    // + custom + custom_post_closure - tips (Tips) - tips_post_closure(Tips) - Fees/Other
    // + Total Discounts + Total Post Closure Discounts
    return new DecimalNumber(informationData.cash || 0)
      .add(
        this.getPostClosureValueWithPreference(
          informationData.cash_post_closure,
        ),
      )
      .add(informationData.credit_debit_gift_total || 0)
      .add(
        this.getPostClosureValueWithPreference(
          informationData.credit_debit_gift_post_closure_total,
        ),
      )
      .add(informationData.custom || 0)
      .add(
        this.getPostClosureValueWithPreference(
          informationData.custom_post_closure,
        ),
      )
      .sub(informationData.tips || 0)
      .sub(
        this.getPostClosureValueWithPreference(
          informationData.tips_post_closure,
        ),
      )
      .sub(informationData.miscellaneous || 0)
      .add(informationData.total_dollars_discounted || 0)
      .add(
        this.getPostClosureValueWithPreference(
          informationData.post_closure_dollars_discounted,
        ),
      );
  }

  public calcInfoReceiptPhysicalDiff(
    informationData: IInformationData,
  ): DecimalNumber {
    // Receipt/Physical Diff = Receipt Total - Physical Sales
    return new DecimalNumber(informationData.receipt_total || 0).sub(
      informationData.physical_sales || 0,
    );
  }

  public calculationData(
    value: IStandWorkSheetItemFullData,
    isUseSnapshot: boolean = false,
  ): GridRowData {
    const totalAvailable: DecimalNumber =
      this.calculateTotalAvailableColumn(value);
    const priceAdjustment: DecimalNumber =
      this.calculatePriceAdjustmentColumn(value);
    const physicalQty: DecimalNumber = this.calculatePhysicalQuantityColumn(
      value,
      totalAvailable,
    );
    const expectedQty: DecimalNumber = this.calculateExpectedQuantityColumn(
      value,
      totalAvailable,
    );
    const varianceQty: DecimalNumber = this.calculateVarianceQuantityColumn(
      value,
      expectedQty,
    );

    return {
      ...value,
      price_adjustment: priceAdjustment,
      total_available: totalAvailable,
      expected_qty: expectedQty,
      physical_qty: physicalQty,
      formattedChargeable: value.chargeable ? '$' : '',
      physical_dollars: this.calculatePhysicalDollarsColumn(
        value,
        priceAdjustment,
        physicalQty,
        isUseSnapshot,
      ),
      variance_qty: varianceQty,
      variance_dollars: this.calculateVarianceDollarsColumn(
        value,
        varianceQty,
        isUseSnapshot,
      ),
      during_event_transfer: this.calcDuringEventTransfer(value),
    };
  }

  public calcInformationData(
    data: GridRowData[],
    currentDocument: IBaseEntityType,
  ): IInformationData {
    // get the not linked recipe sale and discount
    const initialData = {
      physical_sales: new DecimalNumber(
        currentDocument?.unlinked_recipe_tax || 0,
      )
        .add(
          this.getPostClosureValueWithPreference(
            currentDocument?.unlinked_recipe_tax_post_closure,
          ),
        )
        .sub(currentDocument?.unlinked_recipe_discount || 0)
        .sub(
          this.getPostClosureValueWithPreference(
            currentDocument?.unlinked_recipe_discount_post_closure,
          ),
        ),
      terminal_sales: new DecimalNumber(
        currentDocument?.unlinked_recipe_price || 0,
      ).add(currentDocument?.cash_count || 0),
      post_closure_sales: new DecimalNumber(
        currentDocument?.unlinked_recipe_price_post_closure || 0,
      ),
      total_dollars_discounted: new DecimalNumber(
        currentDocument?.unlinked_recipe_discount || 0,
      ),
      post_closure_dollars_discounted: new DecimalNumber(
        currentDocument?.unlinked_recipe_discount_post_closure || 0,
      ),
      total_tax: new DecimalNumber(currentDocument?.unlinked_recipe_tax || 0),
      total_tax_post_closure: new DecimalNumber(
        currentDocument?.unlinked_recipe_tax_post_closure || 0,
      ),
    } as IInformationData;

    return reduce(
      data,
      (sum, cur) => {
        sum.physical_sales = sum.physical_sales.add(
          cur.physical_dollars ? cur.physical_dollars : 0,
        );
        sum.terminal_sales = sum.terminal_sales.add(cur.price ? cur.price : 0);
        sum.post_closure_sales = sum.post_closure_sales.add(
          cur.price_post_closure ? cur.price_post_closure : 0,
        );
        sum.post_closure_dollars_discounted =
          sum.post_closure_dollars_discounted.add(
            cur.discount_post_closure ? cur.discount_post_closure : 0,
          );
        sum.total_dollars_discounted = sum.total_dollars_discounted.add(
          cur.discount ? cur.discount : 0,
        );
        sum.total_tax = sum.total_tax.add(cur.tax || 0);
        sum.total_tax_post_closure = sum.total_tax_post_closure.add(
          cur.tax_post_closure || 0,
        );

        return sum;
      },
      initialData,
    );
  }

  public processSnapshotSWItem(
    item: IStandWorkSheetItemFullData,
  ): IStandWorkSheetItemFullData {
    item.gratis = new DecimalNumber(item.gratis || 0).add(
      this.getPostClosureValueWithPreference(item.gratis_post_closure),
    );
    item.ingredient_spoil = new DecimalNumber(item.ingredient_spoil || 0).add(
      this.getPostClosureValueWithPreference(
        item.ingredient_spoil_post_closure,
      ),
    );

    return item;
  }

  public calcStandWorksheetSum(
    currentDocument: IBaseEntityType,
    standWorksheet: GridRowData[],
  ): IInformationData {
    const calcData = this.calcInformationData(standWorksheet, currentDocument);

    const cashCount = get(currentDocument, 'cash_count');
    const miscellaneous = this.calcMiscellaneous(currentDocument);

    // Convert payments fields
    if (currentDocument.payments) {
      calcData.credit_debit_gift_total = new DecimalNumber(
        get(currentDocument, 'payments.credit'),
      )
        .add(get(currentDocument, 'payments.debit'))
        .add(get(currentDocument, 'payments.gift'));
      calcData.tips = get(currentDocument, 'payments.tips');
      calcData.gift = get(currentDocument, 'payments.gift');
      calcData.credit_debit_gift_post_closure_total = new DecimalNumber(
        get(currentDocument, 'payments.credit_post_closure'),
      )
        .add(get(currentDocument, 'payments.debit_post_closure'))
        .add(get(currentDocument, 'payments.gift_post_closure'));
      calcData.tips_post_closure = get(
        currentDocument,
        'payments.tips_post_closure',
      );
      calcData.cash_post_closure = get(
        currentDocument,
        'payments.cash_post_closure',
      );
      calcData.gift_post_closure = get(
        currentDocument,
        'payments.gift_post_closure',
      );
      calcData.cash = get(currentDocument, 'payments.cash');
      calcData.custom = get(currentDocument, 'payments.custom');
      calcData.custom_post_closure = get(
        currentDocument,
        'payments.custom_post_closure',
      );
    }

    calcData.cash_count = !isNil(cashCount) ? cashCount : null;
    calcData.miscellaneous = !isNil(miscellaneous) ? miscellaneous : null;
    calcData.phys_terminal_diff = calcData.physical_sales
      .sub(calcData.terminal_sales)
      .sub(this.getPostClosureValueWithPreference(calcData.post_closure_sales));
    calcData.cash_terminal_diff =
      this.calcInformationCashTerminalDiff(calcData);
    calcData.receipt_total = this.calcInfoReceiptTotal(calcData);
    calcData.receipt_physical_diff = this.calcInfoReceiptPhysicalDiff(calcData);

    return calcData;
  }

  public getOverrideToolTips(): Record<string, string> {
    return null;
  }

  protected getPostClosureValueWithPreference(
    value: number | string | DecimalNumber,
  ): number | string | DecimalNumber {
    const shouldCalcWithPostClosure =
      this.stateService.getStandWorksheetPostClosureSetting();

    if (!shouldCalcWithPostClosure) return 0;

    return value || 0;
  }

  protected calcMiscellaneous(currentDocument: IBaseEntityType): DecimalNumber {
    return new DecimalNumber(get(currentDocument, 'miscellaneous', 0)).add(
      this.getPostClosureValueWithPreference(
        get(currentDocument, 'miscellaneous_post_closure', 0),
      ),
    );
  }
}
